import { Zap, Battery, TrendingUp, Percent } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { InfoTip } from '@/components/ui/info-tip'
import { SCENARIO_KEYS, SCENARIO_LABELS, SCENARIO_DESCRIPTIONS, type ScenarioKey } from '@/lib/simulator-scenarios'

interface TDSummary {
  totalSavingsEur: number
  avgSavingsPct: number
}

interface SavingsSummaryProps {
  totalSessions: number
  totalEnergyKwh: number
  totalSavingsEur: number
  avgSavingsPct: number
  idaSeriesSummary?: Record<string, { totalSavingsEur: number; avgSavingsPct: number }>
  idaRange?: { minSavingsEur: number; maxSavingsEur: number; minSavingsPct: number; maxSavingsPct: number }
  tdDA?: TDSummary
  tdScenarios?: Partial<Record<ScenarioKey, TDSummary>>
  advanced?: boolean
}

interface StatCardProps {
  label: string
  value: string
  sub?: string
  icon: React.ReactNode
  highlight?: boolean
}

function StatCard({ label, value, sub, icon, highlight }: StatCardProps) {
  return (
    <Card className={highlight ? 'border-green-500/50 bg-green-500/5' : undefined}>
      <CardContent className="p-5 flex items-start gap-4">
        <div
          className={`flex-shrink-0 rounded-lg p-2 ${highlight ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}
        >
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold tracking-tight">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

export function SavingsSummary({
  totalSessions,
  totalEnergyKwh,
  totalSavingsEur,
  avgSavingsPct,
  idaSeriesSummary,
  idaRange,
  tdScenarios,
  advanced = true,
}: SavingsSummaryProps) {
  const idaEntries = idaSeriesSummary ? Object.entries(idaSeriesSummary).sort(([a], [b]) => a.localeCompare(b)) : []
  const hasIda = idaEntries.length > 0
  const availableScenarios = SCENARIO_KEYS.filter((k) => tdScenarios?.[k] !== undefined)
  const hasRange = idaRange !== undefined
  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
        End User Perspective — Flat rate vs. optimised price
        <InfoTip
          content="Answers: how much would the end-customer save if their sessions were charged at the cheapest DA slots instead of paying a fixed tariff? Formula per session: savings = (flat rate × energy) − (optimised DA cost + grid offset × energy)."
          side="right"
        />
      </p>
      <div className={`grid gap-4 ${advanced ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2'}`}>
        {advanced && (
          <StatCard
            label="Total Sessions"
            value={totalSessions.toLocaleString()}
            icon={<Battery className="h-5 w-5" />}
          />
        )}
        {advanced && (
          <StatCard
            label="Total Energy"
            value={`${totalEnergyKwh.toFixed(1)} kWh`}
            icon={<Zap className="h-5 w-5" />}
          />
        )}
        <StatCard
          label="DA Total Savings"
          value={`€${totalSavingsEur.toFixed(2)}`}
          sub="flat rate − optimised DA cost"
          icon={<TrendingUp className="h-5 w-5" />}
          highlight
        />
        <StatCard
          label="DA Avg Savings"
          value={`${avgSavingsPct.toFixed(1)}%`}
          sub="savings ÷ flat cost, per session"
          icon={<Percent className="h-5 w-5" />}
          highlight
        />
      </div>
      {hasIda && idaEntries.map(([key, stats]) => (
        <div key={key} className="grid grid-cols-2 gap-4">
          <StatCard
            label={`${key} Total Savings`}
            value={`€${stats.totalSavingsEur.toFixed(2)}`}
            sub={`vs. flat-rate (${key})`}
            icon={<TrendingUp className="h-5 w-5" />}
            highlight
          />
          <StatCard
            label={`${key} Avg Savings`}
            value={`${stats.avgSavingsPct.toFixed(1)}%`}
            sub={`per session (${key})`}
            icon={<Percent className="h-5 w-5" />}
            highlight
          />
        </div>
      ))}
      {hasRange && advanced && (
        <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-medium text-violet-700 dark:text-violet-400">ID-Cfull Savings Range</span>
          <span className="text-violet-600 dark:text-violet-300">
            €{idaRange!.minSavingsEur.toFixed(2)} – €{idaRange!.maxSavingsEur.toFixed(2)}
          </span>
          <span className="text-muted-foreground">
            ({idaRange!.minSavingsPct.toFixed(1)}% – {idaRange!.maxSavingsPct.toFixed(1)}% avg per session)
          </span>
        </div>
      )}
      {availableScenarios.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide pt-1 flex items-center gap-1.5">
            Trading Desk Perspective — Optimised vs. Immediate Charge
            <InfoTip
              content="Answers: how much does the trading desk save by scheduling into cheap slots, compared to charging immediately after plug-in (filling slots chronologically)? All three scenarios are measured against the same baseline — DA's immediate-charge cost, at raw market price only, no grid fees, taxes, or flat rate involved — so only the optimised cost varies between them. Formula: TD savings = DA immediate-charge cost − scenario's optimised cost. This means Cross-Market, which always finds the cheapest slots of the three, always shows the highest savings."
              side="right"
            />
          </p>
          {availableScenarios.map((key) => {
            const stats = tdScenarios![key]!
            return (
              <div key={key} className="grid grid-cols-2 gap-4">
                <StatCard
                  label={`${SCENARIO_LABELS[key]} TD Savings`}
                  value={`€${stats.totalSavingsEur.toFixed(2)}`}
                  sub={SCENARIO_DESCRIPTIONS[key]}
                  icon={<TrendingUp className="h-5 w-5" />}
                  highlight
                />
                <StatCard
                  label={`${SCENARIO_LABELS[key]} TD Avg Savings`}
                  value={`${stats.avgSavingsPct.toFixed(1)}%`}
                  sub="per session"
                  icon={<Percent className="h-5 w-5" />}
                  highlight
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
